import type { Customer } from '@api/hooks/useProfiles';
import { useProfiles } from '@api/hooks/useProfiles';
import { Skeleton } from '@components/skeleton/Skeleton';
import { useBankProfile } from '@contexts/BankProfileContext';
import {
	CheckedIcon,
	ChevronLeftIcon,
	useCloseOnOutsideAndEsc,
	UserIcon,
} from '@gurupay/gp-ui-library';
import clsx from 'clsx';
import type { FC, FocusEventHandler } from 'react';
import { useRef } from 'react';

interface CompanySwitcherProps {
	onCompanySelect?: () => void;
	className?: string;
}

export const CompanySwitcher: FC<CompanySwitcherProps> = ({
	onCompanySelect,
	className,
}) => {
	const { data, isFetching } = useProfiles();
	const { setSelectedProfile, selectedProfile } = useBankProfile();
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { isOpened, setIsOpened } = useCloseOnOutsideAndEsc([triggerRef, menuRef]);
	const handleToggle = () => setIsOpened((p) => !p);
	const options = data?.customers ?? [];
	const handleSelect = (customer: Customer) => {
		setSelectedProfile(customer);
		setIsOpened(false);
		onCompanySelect?.();
	};
	const hasDropdown = options?.length > 1;
	const isLoading = isFetching && !selectedProfile;
	const handleKeyDonSelect = (customer: Customer, key: string) => {
		if (key === 'Enter' || key === ' ') {
			handleSelect(customer);
		}
	};

	const handleBlur: FocusEventHandler<HTMLDivElement> = (e) => {
		const next = e.relatedTarget as Node | null;
		if (!next || !containerRef.current?.contains(next)) {
			setIsOpened(false);
		}
	};

	return (
		<div
			ref={containerRef}
			className={clsx('relative w-full', className)}
			onBlur={handleBlur}
		>
			<button
				ref={triggerRef}
				aria-haspopup="listbox"
				aria-hidden={!isOpened}
				className={clsx(
					'min-w-0 flex w-full bg-white items-center gap-space-8 rounded-lg p-space-4 py-space-8 text-neutral-800 hover:text-neutral-950 transition-colors text-sm-medium',
					hasDropdown && 'cursor-pointer',
				)}
				disabled={!hasDropdown}
				onClick={handleToggle}
				tabIndex={hasDropdown ? 0 : -1}
				type="button"
			>
				<UserIcon className="flex-[40px] shrink-0 grow-0 bg-neutral-50 rounded-full p-space-8 w-space-40 h-space-40" />
				<div className="flex flex-col items-start max-w-[] flex-1 min-w-0 text-left">
					{isLoading ? (
						<Skeleton className="h-space-16 mb-space-4" />
					) : (
						<span className="w-full text-sm-bold truncate">{data?.name}</span>
					)}
					{isLoading ? (
						<Skeleton className="h-space-16" />
					) : (
						<span className="w-full text-xs-regular truncate">
							{selectedProfile?.name}
						</span>
					)}
				</div>
				{hasDropdown && <ChevronLeftIcon className="rotate-180 p-space-2" />}
			</button>
			{hasDropdown && (
				<div
					ref={menuRef}
					className={clsx(
						'absolute z-10 my-space-4 w-full overflow-auto rounded-lg bg-neutral-white p-space-8 shadow-small transition-all space-y-space-4 top-full origin-top',
						isOpened
							? 'opacity-100 translate-y-0 pointer-events-auto'
							: 'opacity-0 -translate-y-1 pointer-events-none',
					)}
					role="listbox"
					style={{ maxHeight: '14rem' }}
					tabIndex={isOpened ? 0 : -1}
				>
					{options.map((customer) => {
						const { id, name } = customer;
						const isSelected = id === selectedProfile?.id;
						return (
							<div
								key={id}
								aria-selected={isSelected}
								className={clsx(
									'flex cursor-pointer select-none items-center gap-space-12 rounded-md px-space-12 py-space-8 text-sm transition-colors hover:bg-neutral-50',
								)}
								id={`lang-opt-${id}`}
								onClick={() => handleSelect(customer)}
								onKeyDown={(e) => handleKeyDonSelect(customer, e.key)}
								role="option"
								tabIndex={isOpened ? 0 : -1}
							>
								<span className="grow">{name}</span>
								{isSelected && (
									<CheckedIcon className="text-brand-secondary-700 w-space-24 h-space-24" />
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
